import { useEffect, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { TimeSeriesChart } from '../components/TimeSeriesChart';
import './ServiceDashboardPage.css';

interface TotalStatistics {
  total_registered: number;
  current_bottles: number;
  deleted_bottles: number;
  unopened: number;
  opened: number;
  empty: number;
  whisky: number;
  wine: number;
  sake: number;
}

interface StoreStatistics {
  store_id: string | null;
  store_name: string | null;
  bottle_count: number;
  unopened: number;
  opened: number;
  empty: number;
}

interface ConsumptionAnalysis {
  whisky_avg_days?: number;
  wine_avg_days?: number;
  sake_avg_days?: number;
}

interface AnalyticsData {
  total_statistics: TotalStatistics;
  store_statistics: StoreStatistics[];
  consumption_analysis: ConsumptionAnalysis;
  export_timestamp: string;
}

interface TimeSeriesDataPoint {
  date: string;
  total_bottles: number;
  unopened: number;
  opened: number;
  empty: number;
  whisky: number;
  wine: number;
  sake: number;
}

export const ServiceDashboardPage = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesDataPoint[]>([]);
  const [selectedDays, setSelectedDays] = useState<7 | 30 | 90>(30);
  const [loading, setLoading] = useState(true);
  const [timeSeriesLoading, setTimeSeriesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeSeriesError, setTimeSeriesError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const token = localStorage.getItem('idToken');
        if (!token) {
          throw new Error('Not authenticated');
        }

        const apiUrl = import.meta.env.VITE_API_BASE_URL;
        if (!apiUrl) {
          throw new Error('VITE_API_BASE_URL is not configured');
        }
        const response = await fetch(`${apiUrl}/service/analytics/overview`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch analytics data');
        }

        const result = await response.json();
        if (result.success) {
          setData(result.data);
        } else {
          throw new Error(result.message || 'Failed to fetch analytics data');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  useEffect(() => {
    const fetchTimeSeries = async () => {
      setTimeSeriesLoading(true);
      setTimeSeriesError(null);
      try {
        const token = localStorage.getItem('idToken');
        if (!token) {
          throw new Error('Not authenticated');
        }

        const apiUrl = import.meta.env.VITE_API_BASE_URL;
        if (!apiUrl) {
          throw new Error('VITE_API_BASE_URL is not configured');
        }
        const response = await fetch(`${apiUrl}/service/analytics/timeseries?days=${selectedDays}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch time series data');
        }

        const result = await response.json();
        if (result.success) {
          setTimeSeriesData(result.data);
        } else {
          throw new Error(result.message || 'Failed to fetch time series data');
        }
      } catch (err) {
        setTimeSeriesError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setTimeSeriesLoading(false);
      }
    };

    fetchTimeSeries();
  }, [selectedDays]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage error={error} />;
  }

  if (!data) {
    return <ErrorMessage error="No data available" />;
  }

  const { total_statistics, store_statistics, consumption_analysis, export_timestamp } = data;

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>運営者ダッシュボード</h1>
      </div>

      <div className="dashboard-timestamp">
        データ更新日時: {new Date(export_timestamp).toLocaleString('ja-JP')}
      </div>

      <div className="dashboard-section">
        <h2>全体統計</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">累計登録ボトル数</div>
            <div className="stat-value">{total_statistics.total_registered}</div>
            <div className="stat-note">全期間</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">登録ボトル数</div>
            <div className="stat-value">{total_statistics.current_bottles}</div>
            <div className="stat-note">現在アクティブ</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">削除済みボトル数</div>
            <div className="stat-value">{total_statistics.deleted_bottles}</div>
          </div>
        </div>

        <h3>開栓状況（現在）</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">未開栓</div>
            <div className="stat-value">{total_statistics.unopened}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">開栓したボトル数</div>
            <div className="stat-value">{total_statistics.opened}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">空きボトル数</div>
            <div className="stat-value">{total_statistics.empty}</div>
          </div>
        </div>
        <div className="stats-validation">
          <small>
            検証: 未開栓({total_statistics.unopened}) + 開栓({total_statistics.opened}) + 空({total_statistics.empty}) = 登録ボトル数({total_statistics.unopened + total_statistics.opened + total_statistics.empty})
            {total_statistics.unopened + total_statistics.opened + total_statistics.empty === total_statistics.current_bottles ? ' ✓' : ' ⚠️不一致'}
          </small>
        </div>

        <h3>酒類別（現在）</h3>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">ウィスキー</div>
            <div className="stat-value">{total_statistics.whisky}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">ワイン</div>
            <div className="stat-value">{total_statistics.wine}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">日本酒</div>
            <div className="stat-value">{total_statistics.sake}</div>
          </div>
        </div>
      </div>

      <div className="dashboard-section">
        <h2>店舗別統計</h2>
        <table className="store-table">
          <thead>
            <tr>
              <th>店舗名</th>
              <th>登録ボトル数</th>
              <th>未開栓</th>
              <th>開栓したボトル数</th>
              <th>空きボトル数</th>
            </tr>
          </thead>
          <tbody>
            {store_statistics
              .filter(store => store.store_name)
              .map((store, index) => (
                <tr key={index}>
                  <td>{store.store_name}</td>
                  <td>{store.bottle_count}</td>
                  <td>{store.unopened}</td>
                  <td>{store.opened}</td>
                  <td>{store.empty}</td>
                </tr>
              ))}
            <tr className="store-table-total">
              <td><strong>合計</strong></td>
              <td><strong>{store_statistics.reduce((sum, s) => sum + (s.bottle_count || 0), 0)}</strong></td>
              <td><strong>{store_statistics.reduce((sum, s) => sum + (s.unopened || 0), 0)}</strong></td>
              <td><strong>{store_statistics.reduce((sum, s) => sum + (s.opened || 0), 0)}</strong></td>
              <td><strong>{store_statistics.reduce((sum, s) => sum + (s.empty || 0), 0)}</strong></td>
            </tr>
          </tbody>
        </table>
        <div className="stats-validation">
          <small>
            検証: 店舗別合計({store_statistics.reduce((sum, s) => sum + (s.bottle_count || 0), 0)}) = 全体統計の登録ボトル数({total_statistics.current_bottles})
            {store_statistics.reduce((sum, s) => sum + (s.bottle_count || 0), 0) === total_statistics.current_bottles ? ' ✓' : ' ⚠️不一致'}
          </small>
        </div>
      </div>

      <div className="dashboard-section">
        <h2>消費速度分析</h2>
        <div className="stats-grid">
          {consumption_analysis.whisky_avg_days && (
            <div className="stat-card">
              <div className="stat-label">ウィスキー平均消費日数</div>
              <div className="stat-value">{consumption_analysis.whisky_avg_days.toFixed(1)}日</div>
            </div>
          )}
          {consumption_analysis.wine_avg_days && (
            <div className="stat-card">
              <div className="stat-label">ワイン平均消費日数</div>
              <div className="stat-value">{consumption_analysis.wine_avg_days.toFixed(1)}日</div>
            </div>
          )}
          {consumption_analysis.sake_avg_days && (
            <div className="stat-card">
              <div className="stat-label">日本酒平均消費日数</div>
              <div className="stat-value">{consumption_analysis.sake_avg_days.toFixed(1)}日</div>
            </div>
          )}
        </div>
      </div>

      <div className="dashboard-section">
        <div className="section-header">
          <h2>推移グラフ</h2>
          <div className="period-selector">
            <button 
              className={selectedDays === 7 ? 'active' : ''} 
              onClick={() => setSelectedDays(7)}
            >
              7日間
            </button>
            <button 
              className={selectedDays === 30 ? 'active' : ''} 
              onClick={() => setSelectedDays(30)}
            >
              30日間
            </button>
            <button 
              className={selectedDays === 90 ? 'active' : ''} 
              onClick={() => setSelectedDays(90)}
            >
              90日間
            </button>
          </div>
        </div>
        {timeSeriesLoading ? (
          <LoadingSpinner />
        ) : timeSeriesError ? (
          <ErrorMessage error={timeSeriesError} />
        ) : (
          <TimeSeriesChart data={timeSeriesData} />
        )}
      </div>
    </div>
  );
};
